import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Params, Path } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import Button from "@/components/Button";
import { useQuery } from "@apollo/client";
import { GET_ACTIVITY, GET_ACTIVITY_PARTICIPATION } from "../../queries";
import { useMemo } from "react";
import { ParticipationDetailContent, EditParticipationModal, DeleteParticipationModal } from "@/features/participation";
import type { ParticipationDetailData } from "@/features/participation";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import { ObjectDetailPageSkeleton } from "@/components/Skeleton";
import { getParticipationActionPermissions, resolveParticipationReturnPath } from "../participationUtils";
import type { ActivityParticipation } from "../../types";
import { mapParticipationRateFields } from "@/features/participation";

function toParticipationDetailData(
  participation: ActivityParticipation,
  fetchedActivity?: ActivityParticipation["activity"]
): ParticipationDetailData {
  const activityType = participation.activityType || participation.activity?.activityType;

  return {
    title: participation.title,
    description: participation.description,
    creditDescription2: participation.credit?.description2,
    creditType: participation.credit?.creditType,
    series: participation.series,
    hoursServed: participation.hoursServed,
    creditsAwarded: participation.creditsAwarded,
    creditsAppliedFor: participation.creditsAppliedFor,
    startDate: participation.startDate || "",
    endDate: participation.endDate || "",
    location: participation.location,
    submittedAt: participation.submittedAt,
    author: participation.author,
    event: participation.activity?.event,
    ...mapParticipationRateFields({
      role: participation.role,
      attendanceType: participation.attendanceType,
      quantity: participation.quantity,
      isTypeLevelParticipation: participation.isTypeLevelParticipation,
      activityType,
      participationActivity: participation.activity,
      fetchedActivity: fetchedActivity ?? undefined,
      participationTitle: participation.title,
      series: participation.series
    }),
    managingSection: participation.managingSection ? { name: participation.managingSection.name } : undefined
  };
}

function toEditFormDefaultValues(participation: ActivityParticipation): Record<string, string> {
  return {
    activityCode: participation.credit?.activityCode || "",
    activityType: participation.activityType?.title || "",
    person: participation.pgaId || "",
    personId: participation.pgaId || "",
    title: participation.title || "",
    activityDescription: participation.description || participation.credit?.description2 || "",
    totalHours: String(participation.hoursServed ?? participation.creditsAppliedFor ?? ""),
    creditsRequested: String(participation.creditsAppliedFor ?? ""),
    creditsAwarded: String(participation.creditsAwarded ?? ""),
    publishedAt: participation.startDate || "",
    startDate: participation.startDate || "",
    endDate: participation.endDate || "",
    location: participation.location || ""
  };
}

export default function ParticipationDetailPage() {
  const { activityTypeId = "", participantId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl");
  const backToLinkOrDefault = resolveParticipationReturnPath(returnUrl, activityTypeId);
  const [backLink] = useState<LinkProps<Path, Params>>({
    to: backToLinkOrDefault as any,
    title: "Back"
  } as unknown as LinkProps<Path, Params>);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: participationData, loading: participationLoading } = useQuery(GET_ACTIVITY_PARTICIPATION, {
    variables: { id: participantId },
    skip: !participantId
  });

  const participation = useMemo((): ActivityParticipation | undefined => {
    const p = participationData?.activityParticipation;
    if (!p) return undefined;
    return {
      ...p,
      activity: p.activity,
      isTypeLevelParticipation: p.isTypeLevelParticipation,
      eventSlug: p.activity?.event?.slug,
      title: p.title,
      pgaId: p.person?.pgaId,
      activityType: p.activityType ?? p.activity?.activityType,
      creditsAwarded: p.credit?.creditsAccepted ?? 0,
      creditsAppliedFor: p.credit?.creditsAppliedFor ?? 0,
      startDate: p.beganAt,
      endDate: p.endedAt,
      submittedAt: p.createdAt,
      managingSection: p.managingSection
    };
  }, [participationData?.activityParticipation]);

  const activityId = participation?.activity?.id;
  const isTypeLevelParticipation = !!participation?.isTypeLevelParticipation;
  const { data: activityData } = useQuery(GET_ACTIVITY, {
    variables: { id: activityId ?? "" },
    skip: !activityId
  });
  const activity = activityData?.activity;
  const canManageParticipations = useActivityTypePermissions(
    participation?.activityType ?? participation?.activity?.activityType ?? {},
    activity?.managingSection?.id
  );
  const eventSlug = activity?.event?.slug ?? participation?.eventSlug;

  if (participantId && participationLoading) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <ObjectDetailPageSkeleton />
      </FocusedPageLayout>
    );
  }

  if (!participation) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <div className="text-title-large">Participation not found</div>
      </FocusedPageLayout>
    );
  }

  const participationDetailData = toParticipationDetailData(participation!, activity ?? participation.activity);
  const { canShowEdit, canShowDelete } = getParticipationActionPermissions({
    canManageParticipations,
    isTypeLevelParticipation,
    hasCredit: !!participation.credit?.id,
    canEdit: participation.canEdit,
    canDelete: participation.canDelete
  });

  return (
    <FocusedPageLayout
      backLink={backLink}
      actions={
        <>
          {canShowDelete && (
            <Button variant="text" icon="delete" onClick={() => setDeleteModalOpen(true)}>
              Delete Participation
            </Button>
          )}
          {eventSlug ? (
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`/activities/events/${eventSlug}/overview` as Path);
              }}
            >
              View Associated Event
            </Button>
          ) : activityId ? (
            <Button
              variant="outlined"
              onClick={() => {
                navigate(
                  `/activities/activity-types/${activityTypeId}/activity/${activityId}` as Path,
                  activity ? { state: { activity } } : undefined
                );
              }}
            >
              View Activity
            </Button>
          ) : null}
          {canShowEdit && (
            <Button variant="filled" onClick={() => setEditModalOpen(true)}>
              Edit Participation
            </Button>
          )}
        </>
      }
      stickyActions={true}
    >
      {editModalOpen && (
        <EditParticipationModal
          onDismiss={() => setEditModalOpen(false)}
          pgaId={participation?.pgaId ?? ""}
          activityId={participation.credit?.id ?? ""}
          activityParticipationId={participantId}
          isTypeLevelParticipation={isTypeLevelParticipation}
          defaultValues={isTypeLevelParticipation ? undefined : toEditFormDefaultValues(participation!)}
          skipPdrQuery
          additionalRefetchQueries={[{ query: GET_ACTIVITY_PARTICIPATION, variables: { id: participantId } }]}
        />
      )}
      {deleteModalOpen && (
        <DeleteParticipationModal
          onDismiss={() => setDeleteModalOpen(false)}
          activityId={participation.credit?.id ?? undefined}
          activityParticipationId={participantId}
          isTypeLevelParticipation={isTypeLevelParticipation}
          onSuccessNavigate={() => navigate(backToLinkOrDefault as Path)}
        />
      )}
      <ParticipationDetailContent pgaId={participation?.pgaId ?? ""} participation={participationDetailData} />
    </FocusedPageLayout>
  );
}
